import { pushImportBatchGraph } from '../local/sync.js';
import { resolveImportFallback } from '../settings/fallbackPrefs.js';
import { supabase } from '../supabase.js';

// Typed wrappers around the user-facing bulk OCR RPCs. Anything that
// runs as the service role (the worker's claim / complete / fail
// endpoints) lives in the Edge Function, not here.

export type OcrProvider = 'gemini' | 'openai-compatible';
export type RecitationPolicy = 'FALLBACK' | 'FAIL';

export interface OcrKeySummary {
  provider: string;
  /** Named endpoint slug ('default' for the primary key; 'openrouter' etc.
   *  for extra OpenAI-compatible endpoints, each with its own key). */
  endpoint: string;
  key_fingerprint: string;
  base_url: string | null;
  rotated_at: string;
}

export async function setOcrKey(
  provider: OcrProvider,
  rawKey: string,
  baseUrl?: string,
  endpoint = 'default',
): Promise<void> {
  const { error } = await supabase.rpc('ocr_key_set', {
    p_provider: provider,
    p_raw_key: rawKey,
    p_base_url: baseUrl ?? undefined,
    p_endpoint: endpoint,
  });
  if (error) throw error;
}

export async function deleteOcrKey(provider: OcrProvider, endpoint = 'default'): Promise<void> {
  const { error } = await supabase.rpc('ocr_key_delete', {
    p_provider: provider,
    p_endpoint: endpoint,
  });
  if (error) throw error;
}

export interface OcrKeyTestResult {
  ok: boolean;
  /** Plain-language bucket the wizard maps to a friendly message. */
  reason?: 'auth' | 'network' | 'other';
  message?: string;
}

declare global {
  interface Window {
    /** E2E hook: short-circuits the `ocr-key-test` edge call. Mirrors
     *  `window.__cybOcrShim` — returns the canned validation result. */
    __cybOcrKeyTestShim?: (
      provider: OcrProvider,
      rawKey: string,
      baseUrl?: string,
    ) => Promise<OcrKeyTestResult>;
  }
}

/**
 * Validates a freshly-typed OCR key with a live, zero-token provider call
 * (via the `ocr-key-test` edge function) *before* it's stored in Vault, so the
 * onboarding wizard can catch a typo'd / wrong key immediately. The key is sent
 * in the request and never persisted by this call.
 */
export async function validateOcrKey(
  provider: OcrProvider,
  rawKey: string,
  baseUrl?: string,
): Promise<OcrKeyTestResult> {
  if (typeof window !== 'undefined' && window.__cybOcrKeyTestShim) {
    return window.__cybOcrKeyTestShim(provider, rawKey, baseUrl);
  }
  const { data, error } = (await supabase.functions.invoke('ocr-key-test', {
    body: { provider, apiKey: rawKey, baseUrl },
  })) as { data: OcrKeyTestResult | null; error: { message: string } | null };
  if (error) {
    // Transport / function-level failure (not a provider rejection) — treat as
    // a network problem so the wizard tells the user to check their connection.
    return { ok: false, reason: 'network', message: error.message };
  }
  return data ?? { ok: false, reason: 'network', message: 'no response' };
}

/**
 * Force-reset an import item to PENDING for a fresh OCR pass. When `config`
 * is supplied, the RPC also re-snapshots that OCR config onto the parent
 * batch (the worker reads provider/model/prompt from the batch, captured at
 * upload time), so Re-OCR honors the caller's *current* Settings rather than
 * the model/prompt frozen when the batch was first uploaded.
 */
export async function resetImportItem(
  itemId: string,
  config?: Pick<
    EffectiveOcrConfig,
    | 'provider'
    | 'model'
    | 'endpoint'
    | 'prompt'
    | 'fallbackProvider'
    | 'fallbackModel'
    | 'fallbackEndpoint'
    | 'keyOwnerId'
  >,
  opts?: {
    /** Reset the item with needs_fallback=true so the worker runs it against
     *  the batch's FALLBACK config — the per-item "retry with the model I
     *  just picked" path (write the pick via setBatchFallback first). */
    useFallback?: boolean;
  },
): Promise<void> {
  const { error } = await supabase.rpc('import_reset_item', {
    p_item_id: itemId,
    ...(config
      ? {
          p_provider: config.provider,
          p_model: config.model,
          p_endpoint: config.endpoint ?? undefined,
          p_prompt: config.prompt ?? undefined,
          p_fallback_provider: config.fallbackProvider ?? undefined,
          p_fallback_model: config.fallbackModel ?? undefined,
          p_fallback_endpoint: config.fallbackEndpoint ?? undefined,
          p_key_owner_id: config.keyOwnerId ?? undefined,
        }
      : {}),
    ...(opts?.useFallback ? { p_use_fallback: true } : {}),
  });
  if (error) throw error;
}

/**
 * Flip an item's Table-of-Contents flag and re-arm it for a fresh OCR
 * pass, server-side. The client can't push this through the outbox — the
 * push scrub drops any client status change that isn't REVIEWED /
 * DISCARDED, so a page already OCR'd as a recipe could never be re-read
 * as a ToC (and vice versa). This RPC resets the row to PENDING, clears
 * the stale drafts + any prior ToC entries, and sets is_toc in one shot;
 * the caller then kicks the worker so the re-OCR starts immediately.
 */
export async function setImportItemToc(itemId: string, isToc: boolean): Promise<void> {
  const { error } = await supabase.rpc('import_set_item_toc', {
    p_item_id: itemId,
    p_is_toc: isToc,
  });
  if (error) throw error;
}

/**
 * Re-tag an item's page kind (RECIPE / TOC / NOTES) and re-arm it for a fresh
 * OCR pass, server-side. Like {@link setImportItemToc} this can't go through
 * the client push (the scrub blocks the PENDING status flip); the RPC also
 * clears stale drafts / ToC lines / any note already filed from the page so a
 * re-OCR with the new prompt doesn't duplicate. Caller kicks the worker after.
 */
export async function setImportItemKind(
  itemId: string,
  kind: 'RECIPE' | 'TOC' | 'NOTES',
): Promise<void> {
  const { error } = await supabase.rpc('import_set_item_kind', {
    p_item_id: itemId,
    p_kind: kind,
  });
  if (error) throw error;
}

// ---------- bakeoff ----------

export interface BakeoffVariantInput {
  name: string;
  provider: OcrProvider;
  model: string;
  prompt: string;
  base_url?: string;
}

export interface BakeoffVariantRow {
  id: string;
  run_id: string;
  name: string;
  provider: OcrProvider;
  model: string;
  prompt: string;
  base_url: string | null;
  status: 'PENDING' | 'CLAIMED' | 'DONE' | 'FAILED';
  drafts: unknown[] | null;
  raw_text: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cost_usd_micros: number | null;
  latency_ms: number | null;
  error_kind: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface BakeoffRunRow {
  id: string;
  image_storage_path: string | null;
  status: 'OPEN' | 'CLOSED';
  task_kind: 'OCR' | 'REWRITE';
  input_recipe_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function startBakeoff(
  imageStoragePath: string | null,
  variants: ReadonlyArray<BakeoffVariantInput>,
  opts: { taskKind?: 'OCR' | 'REWRITE'; inputRecipeId?: string | null } = {},
): Promise<string> {
  const { data, error } = await supabase.rpc('bakeoff_start', {
    // The OCR path requires a non-null string here; the REWRITE path
    // accepts any value (the body inserts null into the column for
    // REWRITE). Coerce null → '' so the type stays `string` without
    // having to reorder args around the required p_variants.
    p_image_storage_path: imageStoragePath ?? '',
    // PostgREST's typed Json parameter accepts arrays-of-objects fine at
    // runtime, but the generated type alias is too narrow for it.
    p_variants: variants as unknown as never,
    p_task_kind: opts.taskKind ?? 'OCR',
    p_input_recipe_id: opts.inputRecipeId ?? undefined,
  });
  if (error) throw error;
  return data;
}

export async function getBakeoffRun(runId: string): Promise<{
  run: BakeoffRunRow;
  variants: BakeoffVariantRow[];
}> {
  // No bespoke RPC: PostgREST + RLS already scopes both tables to the
  // owner, so two parallel reads are fine. Keeping this in one helper
  // means callers don't have to know about the realtime topic shape.
  const [runQ, vQ] = await Promise.all([
    supabase
      .from('bakeoff_runs')
      .select('id, image_storage_path, status, task_kind, input_recipe_id, created_at, updated_at')
      .eq('id', runId)
      .maybeSingle(),
    supabase
      .from('bakeoff_variants')
      .select(
        'id, run_id, name, provider, model, prompt, base_url, status, drafts, raw_text, prompt_tokens, completion_tokens, cost_usd_micros, latency_ms, error_kind, error_message, created_at, updated_at',
      )
      .eq('run_id', runId)
      .order('sort_index', { ascending: true }),
  ]);
  if (runQ.error) throw runQ.error;
  if (vQ.error) throw vQ.error;
  if (!runQ.data) throw new Error('Bakeoff run not found');
  return {
    run: runQ.data as BakeoffRunRow,
    variants: (vQ.data ?? []) as BakeoffVariantRow[],
  };
}

export async function promoteBakeoffVariant(variantId: string): Promise<void> {
  const { error } = await supabase.rpc('import_bakeoff_promote', { p_variant_id: variantId });
  if (error) {
    const legacy = await supabase.rpc('bakeoff_promote', { p_variant_id: variantId });
    if (legacy.error) throw legacy.error;
    return;
  }
}

export async function seedBakeoffBatch(
  batchId: string,
  variants: ReadonlyArray<BakeoffVariantInput>,
): Promise<void> {
  const { error } = await supabase.rpc('import_bakeoff_seed', {
    p_batch_id: batchId,
    p_variants: variants as unknown as never,
  });
  if (error) throw error;
}

export async function selectBakeoffWinner(itemId: string, variantId: string): Promise<void> {
  const { error } = await supabase.rpc('import_bakeoff_select_winner', {
    p_item_id: itemId,
    p_variant_id: variantId,
  });
  if (error) throw error;
}

export interface ImportBatchVariantRow {
  id: string;
  batch_id: string;
  sort_index: number;
  name: string;
  provider: OcrProvider;
  model: string;
  prompt: string;
  base_url: string | null;
}

export interface ImportItemVariantResultRow {
  id: string;
  item_id: string;
  variant_id: string;
  status: 'PENDING' | 'CLAIMED' | 'DONE' | 'FAILED';
  drafts: unknown[] | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cost_usd_micros: number | null;
  latency_ms: number | null;
  error_kind: string | null;
  error_message: string | null;
}

export async function getBatchVariants(batchId: string): Promise<ImportBatchVariantRow[]> {
  const { data, error } = await supabase
    .from('import_batch_variants')
    .select('id, batch_id, sort_index, name, provider, model, prompt, base_url')
    .eq('batch_id', batchId)
    .order('sort_index', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ImportBatchVariantRow[];
}

export async function getItemVariantResults(itemId: string): Promise<ImportItemVariantResultRow[]> {
  const { data, error } = await supabase
    .from('import_item_variant_results')
    .select(
      'id, item_id, variant_id, status, drafts, prompt_tokens, completion_tokens, cost_usd_micros, latency_ms, error_kind, error_message',
    )
    .eq('item_id', itemId);
  if (error) throw error;
  return (data ?? []) as ImportItemVariantResultRow[];
}

// ---------- user OCR prefs (server-side) ----------

export interface UserOcrPrefs {
  provider: OcrProvider;
  model: string;
  prompt: string;
  updated_at: string;
}

export async function getUserOcrPrefs(): Promise<UserOcrPrefs | null> {
  const { data, error } = await supabase
    .from('user_ocr_prefs')
    .select('provider, model, prompt, updated_at')
    .maybeSingle();
  if (error) throw error;
  return (data as UserOcrPrefs | null) ?? null;
}

export async function setUserOcrPrefs(prefs: {
  provider: OcrProvider;
  model: string;
  prompt: string;
}): Promise<void> {
  const { error } = await supabase.rpc('user_ocr_prefs_set', {
    p_provider: prefs.provider,
    p_model: prefs.model,
    p_prompt: prefs.prompt,
  });
  if (error) throw error;
}

// ---------- shared household OCR config ----------

export interface HouseholdOcrConfig {
  household_id: string;
  ocr_share_enabled: boolean;
  provider: OcrProvider;
  model: string;
  prompt: string | null;
  fallback_provider: OcrProvider | null;
  fallback_model: string | null;
  key_owner_id: string;
  updated_at: string;
}

/** The caller's household OCR config, if any (RLS scopes it to their household). */
export async function getHouseholdOcrConfig(): Promise<HouseholdOcrConfig | null> {
  const { data, error } = await supabase
    .from('household_ocr_config')
    .select(
      'household_id, ocr_share_enabled, provider, model, prompt, fallback_provider, fallback_model, key_owner_id, updated_at',
    )
    .maybeSingle();
  if (error) throw error;
  return (data as HouseholdOcrConfig | null) ?? null;
}

/** Owner-only: set/toggle the household's shared OCR config. */
export async function setHouseholdOcrConfig(params: {
  householdId: string;
  enabled: boolean;
  provider: OcrProvider;
  model: string;
  prompt?: string | null;
  fallbackProvider?: OcrProvider | null;
  fallbackModel?: string | null;
  keyOwnerId?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('set_household_ocr_config', {
    p_household_id: params.householdId,
    p_enabled: params.enabled,
    p_provider: params.provider,
    p_model: params.model,
    p_prompt: params.prompt ?? undefined,
    p_fallback_provider: params.fallbackProvider ?? undefined,
    p_fallback_model: params.fallbackModel ?? undefined,
    p_key_owner_id: params.keyOwnerId ?? undefined,
  });
  if (error) throw error;
}

// ---------- effective OCR config (own, else household-shared) ----------

export interface EffectiveOcrConfig {
  provider: OcrProvider;
  model: string;
  /** Named endpoint the default leg runs against; null = 'default'. */
  endpoint: string | null;
  prompt: string | null;
  fallbackProvider: OcrProvider | null;
  fallbackModel: string | null;
  fallbackEndpoint: string | null;
  /** Where the config came from. 'household' drives the onboarding shortcut. */
  source: 'own' | 'household';
  /** For household source: the member whose key/account is borrowed. */
  keyOwnerId: string | null;
}

/**
 * Resolve the OCR config to use for a bulk import: the member's own prefs
 * (when they also have a key for that provider) win; otherwise the
 * household's shared config if sharing is enabled. Returns null when the
 * user has neither — the import entry then shows the onboarding guide.
 *
 * Never exposes a key: the household row carries only a key_owner_id
 * pointer; the worker resolves the actual Vault key at run time.
 */
export async function getEffectiveOcrConfig(): Promise<EffectiveOcrConfig | null> {
  const [prefs, ownKeys] = await Promise.all([
    getUserOcrPrefs().catch(() => null),
    listOcrKeys().catch(() => [] as OcrKeySummary[]),
  ]);
  // Own-source config carries the user's localStorage "Fallback model"
  // setting; only the household branch below overrides with its own. Kept
  // here (not at call sites) so every caller — including Re-OCR — gets the
  // fallback (previously three call sites forgot to merge it, so a
  // recitation refusal on an own-key batch dead-ended at "Fallback model
  // not configured").
  const ownFallback = resolveImportFallback();
  const hasOwnKeyForPrefs = prefs != null && ownKeys.some((k) => k.provider === prefs.provider);
  if (prefs && hasOwnKeyForPrefs) {
    return {
      provider: prefs.provider,
      model: prefs.model,
      endpoint: null,
      prompt: prefs.prompt,
      fallbackProvider: ownFallback.fallbackProvider,
      fallbackModel: ownFallback.fallbackModel,
      fallbackEndpoint: ownFallback.fallbackEndpoint,
      source: 'own',
      keyOwnerId: null,
    };
  }

  const household = await getHouseholdOcrConfig().catch(() => null);
  if (household && household.ocr_share_enabled) {
    return {
      provider: household.provider,
      model: household.model,
      // Household sharing is deliberately single-endpoint ('default').
      endpoint: null,
      prompt: household.prompt,
      fallbackProvider: household.fallback_provider,
      fallbackModel: household.fallback_model,
      fallbackEndpoint: null,
      source: 'household',
      keyOwnerId: household.key_owner_id,
    };
  }

  // No usable household config: fall back to own prefs if present (lets the
  // form seed even before a key is added).
  if (prefs) {
    return {
      provider: prefs.provider,
      model: prefs.model,
      endpoint: null,
      prompt: prefs.prompt,
      fallbackProvider: ownFallback.fallbackProvider,
      fallbackModel: ownFallback.fallbackModel,
      fallbackEndpoint: ownFallback.fallbackEndpoint,
      source: 'own',
      keyOwnerId: null,
    };
  }
  // No prefs but a key exists → usable with a default model (not onboarding).
  if (ownKeys.length > 0) {
    const provider: OcrProvider =
      ownKeys[0]!.provider === 'openai-compatible' ? 'openai-compatible' : 'gemini';
    return {
      provider,
      model: '',
      endpoint: null,
      prompt: null,
      fallbackProvider: ownFallback.fallbackProvider,
      fallbackModel: ownFallback.fallbackModel,
      fallbackEndpoint: ownFallback.fallbackEndpoint,
      source: 'own',
      keyOwnerId: null,
    };
  }
  // Nothing configured anywhere → caller shows the onboarding guide.
  return null;
}

export async function mergeImportItems(
  primaryId: string,
  absorbIds: readonly string[],
): Promise<void> {
  const { error } = await supabase.rpc('import_merge_items', {
    p_primary_id: primaryId,
    p_absorb_ids: [...absorbIds],
  });
  if (error) throw error;
}

/**
 * Split a committed multi-page recipe back into standalone pages. The inverse
 * of {@link mergeImportItems}: revives the primary's absorbed (DISCARDED)
 * continuation pages to PENDING and clears the primary's extra_storage_paths,
 * so each page OCRs on its own again. Server-side (the PENDING flip can't go
 * through the client push scrub); the caller kicks the worker afterward.
 */
export async function splitImportItem(primaryId: string): Promise<void> {
  const { error } = await supabase.rpc('import_split_item', { p_primary_id: primaryId });
  if (error) throw error;
}

/**
 * Confirm a "Group then OCR" batch's groupings. `groups` is an array of
 * item-id arrays — within each inner array the first id is the primary
 * (recipe gets that page's storage_path) and the rest are absorbed
 * (DISCARDED, their storage_paths appended to the primary's extras).
 * Singletons are passed as one-element arrays. All AWAITING_GROUPING
 * primaries flip to PENDING in one transaction.
 */
export async function finalizeGrouping(
  batchId: string,
  groups: readonly (readonly string[])[],
): Promise<void> {
  // Batch metadata is local-first; the RPC runs on Postgres and needs
  // the row (and items) to exist server-side before it can authorize.
  await pushImportBatchGraph(supabase, batchId);
  const { error } = await supabase.rpc('import_finalize_grouping', {
    p_batch_id: batchId,
    p_groups: groups.map((g) => [...g]),
  });
  if (error) throw error;
}

export async function setRecitationPolicy(
  batchId: string,
  policy: RecitationPolicy,
): Promise<void> {
  const { error } = await supabase.rpc('import_set_recitation_policy', {
    p_batch_id: batchId,
    p_policy: policy,
  });
  if (error) throw error;
}

export async function setBatchFallback(
  batchId: string,
  provider: OcrProvider | null,
  model: string | null,
  endpoint?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('import_set_batch_fallback', {
    p_batch_id: batchId,
    // The function body normalises empty / null inputs to "clear"; the
    // generated type alias erroneously narrows these to required text.
    p_provider: provider as unknown as string,
    p_model: model as unknown as string,
    p_endpoint: endpoint ?? undefined,
  });
  if (error) throw error;
}

/**
 * Retry every recitation/content-filter casualty in the batch against the
 * fallback config. `override` is the "retry with the model I picked" path:
 * it overwrites the batch fallback (provider/endpoint/model) before the
 * reset; the bare call requires a fallback to already be configured.
 */
export async function retryRecitationFailures(
  batchId: string,
  override?: { provider: OcrProvider; model: string; endpoint: string | null },
): Promise<number> {
  const { data, error } = await supabase.rpc('import_retry_recitation_failures', {
    p_batch_id: batchId,
    ...(override
      ? {
          p_fallback_provider: override.provider,
          p_fallback_model: override.model,
          p_fallback_endpoint: override.endpoint ?? undefined,
        }
      : {}),
  });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

/**
 * Reset every OCR_FAILED item in a batch back to PENDING so the worker
 * re-reads them — the batch board's "re-OCR all failures" action. Like
 * {@link resetImportItem}, an optional `config` re-snapshots the caller's
 * current OCR settings onto the batch first, so a bulk re-OCR honors a
 * model / prompt the user changed in Settings after upload. Returns the
 * number of items reset.
 */
export async function retryFailures(
  batchId: string,
  config?: Pick<
    EffectiveOcrConfig,
    | 'provider'
    | 'model'
    | 'endpoint'
    | 'prompt'
    | 'fallbackProvider'
    | 'fallbackModel'
    | 'fallbackEndpoint'
    | 'keyOwnerId'
  >,
): Promise<number> {
  const { data, error } = await supabase.rpc('import_retry_failures', {
    p_batch_id: batchId,
    ...(config
      ? {
          p_provider: config.provider,
          p_model: config.model,
          p_endpoint: config.endpoint ?? undefined,
          p_prompt: config.prompt ?? undefined,
          p_fallback_provider: config.fallbackProvider ?? undefined,
          p_fallback_model: config.fallbackModel ?? undefined,
          p_fallback_endpoint: config.fallbackEndpoint ?? undefined,
          p_key_owner_id: config.keyOwnerId ?? undefined,
        }
      : {}),
  });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

export class OcrWorkerNotConfiguredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OcrWorkerNotConfiguredError';
  }
}

export async function kickOcr(batchId?: string): Promise<void> {
  const { error } = await supabase.rpc('ocr_kick', {
    p_batch_id: batchId ?? undefined,
  });
  if (error) {
    if (error.message?.startsWith('OCR_WORKER_NOT_CONFIGURED')) {
      throw new OcrWorkerNotConfiguredError(error.message);
    }
    throw error;
  }
}

export async function listOcrKeys(): Promise<OcrKeySummary[]> {
  // RLS scopes this to the caller's rows. The `vault_secret_id` column
  // is column-revoked from the `authenticated` role, so we project the
  // safe-to-display fields explicitly rather than using `select('*')`.
  const { data, error } = await supabase
    .from('user_ocr_keys')
    .select('provider, endpoint, key_fingerprint, base_url, rotated_at');
  if (error) throw error;
  return data ?? [];
}

// ---------- saved models (the retry picker's dropdown) ----------

export interface SavedOcrModel {
  id: string;
  provider: OcrProvider;
  /** Named endpoint slug; 'default' = the provider's primary key. */
  endpoint: string;
  model: string;
  label: string | null;
  sort_index: number;
}

export { formatSavedModel } from './savedModelFormat.js';

export async function listSavedOcrModels(): Promise<SavedOcrModel[]> {
  const { data, error } = await supabase
    .from('user_ocr_models')
    .select('id, provider, endpoint, model, label, sort_index')
    .order('sort_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SavedOcrModel[];
}

export async function addSavedOcrModel(input: {
  provider: OcrProvider;
  endpoint: string;
  model: string;
  label?: string;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const ownerId = userData.user?.id;
  if (!ownerId) throw new Error('Sign in required');
  const { error } = await supabase.from('user_ocr_models').insert({
    owner_id: ownerId,
    provider: input.provider,
    endpoint: input.endpoint,
    model: input.model.trim(),
    label: input.label?.trim() || null,
  });
  if (error) throw error;
}

export async function deleteSavedOcrModel(id: string): Promise<void> {
  const { error } = await supabase.from('user_ocr_models').delete().eq('id', id);
  if (error) throw error;
}

// ---------- instruction rewrites ----------

export interface UserRewritePrefs {
  provider: OcrProvider;
  model: string;
  prompt: string;
  updated_at: string;
}

export async function getUserRewritePrefs(): Promise<UserRewritePrefs | null> {
  const { data, error } = await supabase
    .from('user_rewrite_prefs')
    .select('provider, model, prompt, updated_at')
    .maybeSingle();
  if (error) throw error;
  return (data as UserRewritePrefs | null) ?? null;
}

export async function setUserRewritePrefs(prefs: {
  provider: OcrProvider;
  model: string;
  prompt: string;
}): Promise<void> {
  const { error } = await supabase.rpc('user_rewrite_prefs_set', {
    p_provider: prefs.provider,
    p_model: prefs.model,
    p_prompt: prefs.prompt,
  });
  if (error) throw error;
}

export async function startRewrite(input: {
  recipeId: string;
  provider: OcrProvider;
  model: string;
  prompt: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rewrite_start', {
    p_recipe_id: input.recipeId,
    p_provider: input.provider,
    p_model: input.model,
    p_prompt: input.prompt,
  });
  if (error) throw error;
  return data;
}

export async function cancelRewrite(jobId: string): Promise<void> {
  const { error } = await supabase.rpc('rewrite_cancel', { p_job_id: jobId });
  if (error) throw error;
}

export async function kickRewrite(recipeId?: string): Promise<void> {
  const { error } = await supabase.rpc('rewrite_kick', {
    p_recipe_id: recipeId ?? undefined,
  });
  if (error) {
    // Same error class as kickOcr — the underlying vault secret is shared,
    // so the configuration-missing case looks identical.
    if (error.message?.startsWith('OCR_WORKER_NOT_CONFIGURED')) {
      throw new OcrWorkerNotConfiguredError(error.message);
    }
    throw error;
  }
}

// ---------- recipe remix ----------

export interface UserRemixPrefs {
  provider: OcrProvider;
  model: string;
  prompt: string;
  updated_at: string;
}

export async function getUserRemixPrefs(): Promise<UserRemixPrefs | null> {
  const { data, error } = await supabase
    .from('user_remix_prefs')
    .select('provider, model, prompt, updated_at')
    .maybeSingle();
  if (error) throw error;
  return (data as UserRemixPrefs | null) ?? null;
}

export async function setUserRemixPrefs(prefs: {
  provider: OcrProvider;
  model: string;
  prompt: string;
}): Promise<void> {
  const { error } = await supabase.rpc('user_remix_prefs_set', {
    p_provider: prefs.provider,
    p_model: prefs.model,
    p_prompt: prefs.prompt,
  });
  if (error) throw error;
}

export async function startRemix(input: {
  recipeId: string;
  provider: OcrProvider;
  model: string;
  /** System-prompt override; '' falls back to the worker's REMIX_PROMPT. */
  prompt: string;
  /** The user's freeform remix request for this turn. */
  instruction: string;
  /** The working recipe to transform (source on turn 1, prior draft on chat). */
  inputRecipeJson: unknown;
}): Promise<string> {
  const { data, error } = await supabase.rpc('remix_start', {
    p_recipe_id: input.recipeId,
    p_provider: input.provider,
    p_model: input.model,
    p_prompt: input.prompt,
    p_instruction: input.instruction,
    // PostgREST's typed Json parameter accepts an object fine at runtime, but
    // the generated type alias is too narrow (see startBakeoff p_variants).
    p_input_recipe_json: input.inputRecipeJson as never,
  });
  if (error) throw error;
  return data;
}

export async function cancelRemix(jobId: string): Promise<void> {
  const { error } = await supabase.rpc('remix_cancel', { p_job_id: jobId });
  if (error) throw error;
}

export async function kickRemix(recipeId?: string): Promise<void> {
  const { error } = await supabase.rpc('remix_kick', {
    p_recipe_id: recipeId ?? undefined,
  });
  if (error) {
    // Same error class as kickOcr/kickRewrite — the underlying vault secret
    // is shared, so the configuration-missing case looks identical.
    if (error.message?.startsWith('OCR_WORKER_NOT_CONFIGURED')) {
      throw new OcrWorkerNotConfiguredError(error.message);
    }
    throw error;
  }
}
